import { Button, Empty, Input, message, Spin } from 'antd';
import { MobileHeaderComp, PostCardComp } from 'components';
import { GlobalContext } from 'context';
import { PATH } from 'data';
import { Fragment, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { TbSearch, TbSquarePlus } from 'react-icons/tb';
import { Link } from 'react-router-dom';
import { useRealTimeState, useThen } from 'scripts';
import { __LoadedPage, __PageQueryKey } from 'scripts/paginationQuery';
import { PaginationType, PostType, ThemeEnum } from 'types';
import Styles from './myAds.module.scss';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { InView } from 'react-intersection-observer';
import { __FetchMyAds, __MY_ADS_PAGE_SIZE } from './myAds.script';

const __MY_ADS_SEARCH_DEBOUNCE_MS = 700;

function MyAdsComp() {
    const { theme } = useContext(GlobalContext);
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    useEffect(() => {
        const id = setTimeout(() => setSearch(searchInput.trim()), __MY_ADS_SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(id);
    }, [searchInput]);

    const queryKey = useMemo(() => ['posts', 'panel', search] as const, [search]);
    const pageQueryKey = useMemo(() => __PageQueryKey(queryKey), [queryKey]);
    const queryClient = useQueryClient();
    const [page, setPageState] = useState(
        () =>
            queryClient.getQueryData<number>(pageQueryKey) ??
            __LoadedPage(queryClient.getQueryData<PaginationType<PostType<'list'>>>(queryKey), __MY_ADS_PAGE_SIZE) ??
            1,
    );
    const setPage = useCallback(
        (value: React.SetStateAction<number>) => {
            setPageState((prev) => {
                const next = typeof value === 'function' ? value(prev) : value;
                queryClient.setQueryData(pageQueryKey, next);
                return next;
            });
        },
        [queryClient, pageQueryKey],
    );
    const query = useQuery({
        queryKey,
        queryFn: () => {
            const prev = page > 1 ? queryClient.getQueryData<PaginationType<PostType<'list'>>>(queryKey) : undefined;
            return __FetchMyAds(page, prev, search || undefined);
        },
        staleTime: 30 * 60 * 1000,
    });
    const loadMore = useRealTimeState(false);
    const prevSearchRef = useRef(search);

    useThen(() => {
        const searchChanged = prevSearchRef.current !== search;
        prevSearchRef.current = search;
        if (searchChanged && page !== 1) {
            setPage(1);
            return;
        }

        queryClient
            .cancelQueries({ queryKey })
            .then(() => {
                if (page === 1) {
                    queryClient.setQueryData(queryKey, undefined);
                    queryClient.setQueryData(pageQueryKey, 1);
                } else loadMore.set(true);
                return query.refetch();
            })
            .catch(() => message.error('دریافت آگهی‌ها با خطا مواجه شد. لطفا بعدا تلاش کنید.'))
            .finally(() => loadMore.set(false));
    }, [page, search]);

    const posts = query.data;
    const searchInputField = (
        <Input
            allowClear
            placeholder="جستجو در آگهی‌های من"
            className={Styles['search-container']}
            prefix={
                <TbSearch size={28} className={`pl-1 pointer ${theme === ThemeEnum.light ? 't-black1' : 't-gray2'}`} />
            }
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onClear={() => setSearchInput('')}
        />
    );

    return (
        <>
            <MobileHeaderComp title="فروشگاه من" />
            {!posts ? (
                <div className={`w-100 pv-9 center-content flex-1`}>
                    <Spin />
                </div>
            ) : posts.count > 0 ? (
                <div className={`${Styles['main-content']} flex-column`}>
                    {searchInputField}
                    <div className={`${Styles['ads-layout']}`}>
                        {posts.data.map((post, index) => (
                            <Fragment key={index}>
                                {posts.num_of_pages !== page && index >= posts.data.length - 6 ? (
                                    <InView
                                        as="div"
                                        onChange={(inView) => {
                                            if (inView && !loadMore.get()) setPage(page + 1);
                                        }}
                                    >
                                        <PostCardComp data={post} />
                                    </InView>
                                ) : (
                                    <PostCardComp isPanel key={post.slug} data={post} />
                                )}
                            </Fragment>
                        ))}
                        {Array.from({ length: 3 }).map((_, index) => (
                            <div key={index} />
                        ))}
                    </div>
                    {posts.num_of_pages !== page ? (
                        <InView
                            as="div"
                            className="center-content pv-2 w-100"
                            onChange={(inView) => {
                                if (inView && !loadMore.get()) setPage(page + 1);
                            }}
                        >
                            <Spin />
                        </InView>
                    ) : null}
                </div>
            ) : (
                <div className={`${Styles['main-content']} flex-column`}>
                    {searchInputField}
                    <Empty
                        description={
                            search
                                ? 'آگهی‌ای با این عنوان یافت نشد.'
                                : 'تا اکنون آگهی ثبت نشده است. برای ثبت اگهی کلیک کنید.'
                        }
                        className={`${Styles['empty']} center-content flex-column`}
                    >
                        {!search ? (
                            <Link to={PATH.panel.addPost.index()}>
                                <Button icon={<TbSquarePlus size={20} />}>{'ثبت آگهی'}</Button>
                            </Link>
                        ) : null}
                    </Empty>
                </div>
            )}
        </>
    );
}

export default MyAdsComp;
